import { morph, MORPH_OPTIONS } from '@theme/morph';

window.addEventListener("onPredictLoad", function (event) {
  window.wairPredict = event.detail.predict;
});

window.theme = window.theme || {};
theme.current = theme.current || {};

/**
 * Builds a responsive srcset string for an image object.
 * Ported from the source theme — required by ProductStitching.replaceImages.
 * @param {{src: string, width: number}} imageObject
 * @param {number[]} widths
 * @returns {string}
 */
function generateSrcset(imageObject, widths = []) {
  let src = imageObject["src"];
  if (!/^https:\/\//i.test(imageObject["src"])) {
    src = `https:${imageObject["src"]}`;
  }
  const imageUrl = new URL(src);
  return widths
    .filter((width) => width <= imageObject["width"])
    .map((width) => {
      imageUrl.searchParams.set("width", width.toString());
      return `${imageUrl.href} ${width}w`;
    })
    .join(", ");
}

/**
 * Re-initializes the product section after a swatch swap.
 * The source theme used the slate framework here; Horizon builds its product UI
 * from web components that re-initialize automatically once their markup is
 * inserted into the DOM, so we only guard the legacy hooks to avoid throwing.
 * @param {object} product
 */
function reinitializeProductSection(product) {
  if (typeof slate !== 'undefined' && slate.Sections && window.theme?.Product) {
    new slate.Sections().register('product', theme.Product);
  }
  if (typeof reinitProductOptions === 'function') {
    setTimeout(() => reinitProductOptions(product), 500);
  }
  
  // Reinitialize WAIR size quiz
  const wairPredict = window.predict || window.wairPredict;
  if (wairPredict && typeof wairPredict.render === 'function') {
    const brandKey = Shopify?.shop || window.Shopify?.shop;
    if (brandKey && product) {
      try {
        let imageUrl = product.featured_image || product.featured_media?.preview_image?.src || '';
        if (imageUrl && imageUrl.startsWith('//')) {
          imageUrl = 'https:' + imageUrl;
        }
        wairPredict.render({
          brandKey: brandKey,
          product: {
            key: product.handle,
            name: product.title,
            image: imageUrl
          }
        });
      } catch (error) {
        console.error('Failed to reinitialize WAIR size advisor:', error);
      }
    }
  }
}

const ProductStitching = class extends HTMLElement {
  constructor() {
    super();
    window.theme.preloadedSections = window.theme.preloadedSections || {};
    this.preloadedSections = window.theme.preloadedSections;
    this.container = this.querySelector('.product-stitching__color-list');
    this.settings = this.getSettings('#swatches-settings');
    this.template = this.dataset.template || '';
    // Derive bundle state from this element's own data attribute (set by the
    // Liquid) instead of a hardcoded global, so normal PDPs use the standard
    // section-swap path and their swatch media updates correctly.
    this.isBundleTemplate = this.dataset.isBundle === 'true';
    if (this.template === 'product' && this.isBundleTemplate !== true) {
      this.initPopStateListener(); 
    }
    const hideIfEmpty = this.settings.hideIfEmpty;

    // Re-apply the swatch limit when crossing the mobile/desktop breakpoint.
    this.swatchLimitMobileQuery = window.matchMedia('(max-width: 749px)');
    this.swatchLimitMobileQuery.addEventListener('change', () => this.applySwatchLimit());
    // Limit the server-rendered list right away to avoid a flash of all swatches.
    this.applySwatchLimit();

    if (this.dataset.groupTag) {
      const groupTag = this.dataset.groupTag;
      const cacheKey = `${this.settings.allProductsCollection}/${groupTag}`;
      
      window.theme.productStitchingGroups = window.theme.productStitchingGroups || {};
      
      let fetchPromise;
      if (window.theme.productStitchingGroups[cacheKey]) {
        fetchPromise = Promise.resolve(window.theme.productStitchingGroups[cacheKey]);
      } else {
        fetchPromise = fetch(`/collections/${this.settings.allProductsCollection}/${encodeURIComponent(groupTag)}?view=data`)
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.json();
            })
            .then((responseJson) => {
              window.theme.productStitchingGroups[cacheKey] = responseJson;
              return responseJson;
            });
      }
      
      fetchPromise
          .then((responseJson) => {
            const domHandles = Array.from(this.container.querySelectorAll('.product-stitching__color-item a')).map(a => a.getAttribute('data-handle')).filter(Boolean);
            const jsonHandles = responseJson
              .filter(p => p.available || !this.settings.hideOptionsOutOfStock)
              .map(p => p.handle);
              
            const isMatch = domHandles.length === jsonHandles.length && domHandles.every((h, i) => h === jsonHandles[i]);
            if (!isMatch) {
              this.initSwatches(responseJson);
            }
            this.attachColorSwatchEventListeners(responseJson);
            this.setCurrent();
            this.applySwatchLimit();
            this.hideIfEmpty(hideIfEmpty);
            this.preloadSections(responseJson);
          })
          .catch((error) => {
            console.error('Failed to load swatches:', error);
            this.hideIfEmpty(hideIfEmpty);
          });
    } else {
      this.applySwatchLimit();
      this.hideIfEmpty(hideIfEmpty);
    }
  }

  /**
   * Limits the number of visible colour swatches and appends a "+N" counter for
   * the remaining items — 6 on desktop, 4 on mobile (≤749px). Functionality
   * only; styling of the counter is left to CSS. Clicking/activating the counter
   * reveals the hidden swatches.
   */
  applySwatchLimit() {
    if (!this.container) return;

    const limit = this.swatchLimitMobileQuery && this.swatchLimitMobileQuery.matches ? 4 : 6;

    // Drop any previously appended counter before recalculating.
    this.container.querySelector('.product-stitching__color-item--more')?.remove();

    let items = Array.from(this.container.querySelectorAll('.product-stitching__color-item'));

    // Keep the currently selected swatch visible even if it sits past the limit.
    const activeItem = this.container.querySelector(
      '.product-stitching__color-item--active, .product-stitching__color-item.current'
    );
    if (activeItem && items.indexOf(activeItem) >= limit) {
      this.container.insertBefore(activeItem, this.container.firstChild);
      items = Array.from(this.container.querySelectorAll('.product-stitching__color-item'));
    }

    items.forEach((item, index) => {
      item.style.display = index < limit ? '' : 'none';
    });

    const hiddenCount = items.length - limit;
    if (hiddenCount <= 0) return;

    const more = document.createElement('li');
    more.className = 'product-stitching__color-item product-stitching__color-item--more';
    more.textContent = `+${hiddenCount} more`;
    more.setAttribute('role', 'button');
    more.setAttribute('tabindex', '0');

    const reveal = () => {
      items.forEach((item) => { item.style.display = ''; });
      more.remove();
    };
    more.addEventListener('click', reveal);
    more.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        reveal();
      }
    });

    this.container.appendChild(more);
  }
  
  initPopStateListener() {
    window.addEventListener('popstate', (event) => this.handlePopState(event));
  }
  
  handlePopState(event) {
    if (event.state && event.state.path) {
      const url = event.state.path.replaceAll('/products/', '');
      this.updateSection(url);
    }
  }
  
  hideIfEmpty(hideIfEmpty) {
    if (!hideIfEmpty) return;
    
    const swatches = this.querySelectorAll('.product-stitching__color-list li');
    if (swatches.length > 0) return;
    
    this.classList.add("swatch-empty--hide");
  }
  
  getSettings(selector) {
    const settingsRawJson = document.querySelector(`${selector}`);
    if (!settingsRawJson) return {};
    return JSON.parse(settingsRawJson.innerHTML);
  }
  
  initSwatches(products) {
    let html = "";
    
    products.forEach(product => {
      const isAvailable = product.available || !this.settings.hideOptionsOutOfStock;
      if (isAvailable) {
        const swatchMedia = this.getSwatchMedia(product);
        const isActive = this.dataset.productHandle === product.handle;
        const liClass = isActive
            ? "current product-stitching__color-item--active product-stitching__color-item"
            : "product-stitching__color-item";
        
        html += `
        <li class="${liClass}">
          <a style="${swatchMedia}"
             data-handle="${product.handle}"
             href="${product.url}"
             data-href="/products/${product.handle}"
             data-product-id="${product.id}">
          </a>
        </li>
      `;
      }
    });
    
    if (html) {
      this.container.innerHTML = html;
    }
  }

  preloadSections(products) {
    if (!this.dataset.sectionId) return;
    
    products.forEach(product => {
      if (product.handle !== this.dataset.productHandle) {
        if (!this.preloadedSections[product.handle]) {
          fetch(`/products/${product.handle}?sections=${this.dataset.sectionId}`)
              .then(response => {
                if (!response.ok) {
                  throw new Error(`Error fetching section: ${response.statusText}`);
                }
                return response.json();
              })
              .then(data => {
                this.preloadedSections[product.handle] = data;
              })
              .catch(error => {
                console.error(`Request failed for ${product.handle}:`, error);
              });
        }
      }
    });
  }

  updateActiveSwatchFrom(newStitching) {
    const oldItems = Array.from(this.container.querySelectorAll('.product-stitching__color-item'));
    
    const activeItem = newStitching.querySelector('.product-stitching__color-item--active a, .product-stitching__color-item.current a');
    const activeHandle = activeItem ? activeItem.getAttribute('data-handle') : null;
    
    if (activeHandle) {
      oldItems.forEach(item => {
        const a = item.querySelector('a');
        if (a) {
          const handle = a.getAttribute('data-handle');
          if (handle === activeHandle) {
            item.classList.add('current', 'product-stitching__color-item--active');
          } else {
            item.classList.remove('current', 'product-stitching__color-item--active');
          }
        }
      });
    }
    
    this.applySwatchLimit();
  }
  
  getSwatchMedia(product) {
    let swatchMedia = `background-image: url(${product.featured_image})`;
    switch (this.settings.swatchType) {
      case "hex_colour": {
        // The ?view=data template (collection.data.liquid) exposes the hex
        // colour under `color_swatch_metafield`; keep `color_hex` as a fallback.
        const colourHex = product.color_hex || product.color_swatch_metafield;
        if (colourHex) swatchMedia = `background-color: ${colourHex}`;
        break;
      }
      case "image_colour": {
        // Likewise the swatch image URL is exposed as `image_swatch_metaobject`.
        const colourImage = product.color_image || product.image_swatch_metaobject;
        if (colourImage) swatchMedia = `background-image: url(${colourImage})`;
        break;
      }
      default:
        break;
    }
    return swatchMedia;
  }
  
  updateSection(url) {
    const sectionId = this.dataset.sectionId;
    const sectionContainer = document.querySelector(`#shopify-section-${sectionId}`);
    if (!sectionContainer) return;
    
    const oldStitching = this;
    
    if (this.preloadedSections[url]) {
      this.replaceSection(sectionContainer, url, sectionId);
      const newStitching = document.querySelector(`#shopify-section-${sectionId} product-stitching`);
      if (oldStitching !== newStitching) {
        this.runOnceAfterUpdate();
      }
    } else {
      console.warn(`No preloaded section found for: ${url}. Fetching dynamically...`);
      fetch(`/products/${url}?sections=${sectionId}`)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            this.preloadedSections[url] = data;
            this.replaceSection(sectionContainer, url, sectionId);
            const newStitching = document.querySelector(`#shopify-section-${sectionId} product-stitching`);
            if (oldStitching !== newStitching) {
              this.runOnceAfterUpdate();
            }
          })
          .catch(error => {
            console.error('Failed to load section dynamically:', error);
          });
    }
  }
  
  replaceSection(sectionContainer, url, sectionId) {
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = this.preloadedSections[url][`${sectionId}`];
    const newSection = tempContainer.querySelector(`#shopify-section-${sectionId}`);    
    
    // Save current add to cart button text to handle localization and merchant custom settings
    const currentAddToCartButton = sectionContainer.querySelector('product-form-component [ref="addToCartButton"]');
    let addToCartText = 'Add to cart';
    let soldOutText = 'Sold out';
    if (currentAddToCartButton) {
      const btnTextEl = currentAddToCartButton.querySelector('.add-to-cart-text__content span span');
      if (btnTextEl) {
        const currentText = btnTextEl.textContent.trim();
        if (currentText.toLowerCase().includes('add') || currentText.toLowerCase().includes('buy') || currentText.toLowerCase().includes('bag')) {
          addToCartText = currentText;
        } else if (currentText.toLowerCase().includes('sold') || currentText.toLowerCase().includes('out')) {
          soldOutText = currentText;
        }
      }
    }

    const currentChildProductSection = this.closest('.product-single__offer');
    const currentProductHandle = this.dataset.productHandle;
    const childProduct = tempContainer.querySelector(`.product-single__offer[data-product-handle="${url}"]`);
    if (this.isBundleTemplate === true) {
      if (currentChildProductSection && childProduct) {
        childProduct.classList.add('product-single__offer--child', 'od-product-single__offer--child');
        const childProductInfo = childProduct.querySelector('.child-product-info');
        if (childProductInfo) {
          childProductInfo.style.removeProperty('display');
        }
        const productSinglePrice = childProduct.querySelector('.child-product-info ~ .product-single__price');
        if (productSinglePrice) {
          productSinglePrice.remove();
        }
        const exclusiveProductParent = childProduct.querySelector('.exclusive_product-parent');
        if (exclusiveProductParent) {
          exclusiveProductParent.remove();
        }
        const productStitching = childProduct.querySelector('product-stitching[data-is-bundle="false"]');
        if (productStitching) {
          productStitching.setAttribute('data-is-bundle', true);
        }
        const klaviyoTrigger = childProduct.querySelector('.klaviyo-trigger');
        if (klaviyoTrigger) {
          klaviyoTrigger.remove();
        }
        
        const productRawJson = childProduct.querySelector(`[data-offer-json]`);
        
        currentChildProductSection.replaceWith(childProduct);
        
        new Promise((resolve) => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              resolve();
            }, 0);
          });
        }).then(() => {
          if (productRawJson) {
            const productJson = JSON.parse(productRawJson.innerHTML);
            reinitializeProductSection(productJson);
          }
          document.dispatchEvent(new CustomEvent("overdose:child-product-stitching-loaded"));
        });
      }
    } else {
      let selectedVariant = null;
      if (newSection) {
        // Carry over selected options (e.g. size/length) across stitching products
        const selectedOptionsMap = {};
        const oldChecked = sectionContainer.querySelectorAll('select, fieldset input:checked');
        oldChecked.forEach(el => {
          const index = el.dataset.fieldsetIndex || el.getAttribute('data-fieldset-index');
          if (index !== undefined && index !== null) {
            if (el.tagName === 'SELECT') {
              const selectedOpt = el.options[el.selectedIndex];
              if (selectedOpt) {
                selectedOptionsMap[index] = selectedOpt.value;
              }
            } else {
              selectedOptionsMap[index] = el.value;
            }
          }
        });

        // Apply selected options to newSection
        for (const [index, value] of Object.entries(selectedOptionsMap)) {
          const newSelect = newSection.querySelector(`select[data-fieldset-index="${index}"]`);
          if (newSelect) {
            Array.from(newSelect.options).forEach(opt => {
              if (opt.value === value) {
                opt.setAttribute('selected', 'selected');
                newSelect.selectedIndex = opt.index;
              } else {
                opt.removeAttribute('selected');
              }
            });
            const legendValue = newSelect.closest('fieldset')?.querySelector('.variant-option__swatch-value');
            if (legendValue) {
              legendValue.textContent = value;
            }
          } else {
            const newRadios = newSection.querySelectorAll(`input[type="radio"][data-fieldset-index="${index}"]`);
            if (newRadios.length > 0) {
              const hasTargetValue = Array.from(newRadios).some(r => r.value === value);
              if (hasTargetValue) {
                newRadios.forEach(radio => {
                  if (radio.value === value) {
                    radio.checked = true;
                    radio.setAttribute('checked', 'checked');
                    radio.setAttribute('data-current-checked', 'true');
                    radio.dataset.currentChecked = 'true';
                  } else {
                    radio.checked = false;
                    radio.removeAttribute('checked');
                    radio.setAttribute('data-current-checked', 'false');
                    radio.dataset.currentChecked = 'false';
                  }
                });
                const legendValue = newRadios[0].closest('fieldset')?.querySelector('.variant-option__swatch-value');
                if (legendValue) {
                  legendValue.textContent = value;
                }
              }
            }
          }
        }

        // Update the variant ID input in the product form to match the newly selected options
        const newVariantPicker = newSection.querySelector('variant-picker');
        if (newVariantPicker) {
          const variantsEl = newVariantPicker.querySelector('.product-variants-json');
          const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
          if (variants.length > 0) {
            const checkedInputs = Array.from(newVariantPicker.querySelectorAll('select option[selected], fieldset input:checked'));
            selectedVariant = variants.find(v => {
              return checkedInputs.every(input => {
                let optionIndex = Number(input.dataset.fieldsetIndex);
                if (input.dataset.inputId) {
                  const parts = input.dataset.inputId.split('-');
                  if (parts.length > 0) {
                    optionIndex = Number(parts[0]) - 1;
                  }
                }
                if (Number.isNaN(optionIndex)) return true;
                return v.options[optionIndex] === input.value;
              });
            });
            
            if (selectedVariant) {
              const productForm = newVariantPicker.closest('product-form-component') || newSection.querySelector(`product-form-component[data-section-id="${sectionId}"]`);
              if (productForm) {
                const variantIdInput = productForm.querySelector('input[name="id"]');
                if (variantIdInput) {
                  variantIdInput.value = selectedVariant.id;
                  variantIdInput.setAttribute('value', selectedVariant.id);
                }
              }
              
              const urlParams = new URL(window.location.href);
              urlParams.searchParams.set('variant', selectedVariant.id);
              history.replaceState(null, '', urlParams.toString());
            }
          }
        }

        // Update the button inside newSection to match selectedVariant availability and text
        const newButton = newSection.querySelector('product-form-component [ref="addToCartButton"]');
        const newButtonTextEl = newButton?.querySelector('.add-to-cart-text__content span span');
        if (selectedVariant && newButton) {
          if (selectedVariant.available) {
            newButton.removeAttribute('disabled');
            if (newButtonTextEl) newButtonTextEl.textContent = addToCartText;
          } else {
            newButton.setAttribute('disabled', 'disabled');
            if (newButtonTextEl) newButtonTextEl.textContent = soldOutText;
          }
        }

        // Replace Klaviyo reviews widget containers in the DOM with new ones to trigger their re-evaluation / MutationObservers
        const replaceSelectors = [
          '.klaviyo-star-rating-widget',
          '.klaviyo-reviews-placeholder',
          '[id*="klaviyo"]',
          '[class*="klaviyo"]',
          'variant-picker'
        ];
        
        replaceSelectors.forEach(selector => {
          const oldElements = sectionContainer.querySelectorAll(selector);
          oldElements.forEach((oldEl) => {
            let newEl = oldEl.id ? newSection.querySelector(`#${oldEl.id}`) : null;
            if (!newEl) {
              const allNew = Array.from(newSection.querySelectorAll(selector));
              const allOld = Array.from(sectionContainer.querySelectorAll(selector));
              const index = allOld.indexOf(oldEl);
              newEl = allNew[index] || allNew[0];
            }
            
            if (newEl) {
              const clone = newEl.cloneNode(true);
              oldEl.replaceWith(clone);
            }
          });
        });

        // Update attributes on the existing container so it stays mounted in the
        // DOM — avoids a full remove/insert cycle that causes all variant option
        // buttons to flash (blink) due to CSS transition re-triggers.
        for (const attr of Array.from(newSection.attributes)) {
          sectionContainer.setAttribute(attr.name, attr.value);
        }
        // Remove attributes that exist on the old container but not the new one.
        for (const attr of Array.from(sectionContainer.attributes)) {
          if (!newSection.hasAttribute(attr.name)) {
            sectionContainer.removeAttribute(attr.name);
          }
        }
        
        // Replace the media gallery. Do not morph it. A morph keeps the old
        // `<slideshow-component>` connected, so its scroller and observers stay
        // bound to the old slides and swipe stops on mobile. A new element runs
        // `connectedCallback` again and sets up the slideshow.
        const oldMediaGallery = sectionContainer.querySelector('media-gallery');
        const newMediaGallery = newSection.querySelector('media-gallery');
        if (oldMediaGallery && newMediaGallery) {
          oldMediaGallery.replaceWith(newMediaGallery.cloneNode(true));
        }

        // Morph the rest of the section, leaving `<product-stitching>` element untouched (since morph skips it)
        morph(sectionContainer, newSection, MORPH_OPTIONS);
        
        // Synchronously update the current `<product-stitching>` element using details from `newSection`
        const currentStitching = this;
        const newStitching = newSection.querySelector('product-stitching');
        if (newStitching) {
          // Copy attributes
          for (const attr of Array.from(newStitching.attributes)) {
            currentStitching.setAttribute(attr.name, attr.value);
          }
          // Copy the legend/label if it exists
          const oldLegend = currentStitching.querySelector('.option--label');
          const newLegend = newStitching.querySelector('.option--label');
          if (oldLegend && newLegend) {
            oldLegend.innerHTML = newLegend.innerHTML;
          }
          // Update active swatch class in place to avoid blinking
          currentStitching.updateActiveSwatchFrom(newStitching);
        }
      } else {
        sectionContainer.innerHTML = this.preloadedSections[url][`${this.dataset.sectionId}`];
      }
      const eventHtml = newSection;
      new Promise((resolve) => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            resolve();
          }, 0);
        });
      }).then(() => {
        const productJson = this.getSettings('[data-product-json]');
        reinitializeProductSection(productJson);

        // Dispatch variant:update event to update the product form and add to cart button
        if (selectedVariant && productJson) {
          const variantUpdateEvent = new CustomEvent('variant:update', {
            bubbles: true,
            detail: {
              resource: selectedVariant,
              sourceId: '',
              data: {
                html: eventHtml,
                productId: productJson.id.toString(),
                newProduct: {
                  id: productJson.id.toString(),
                  url: productJson.url
                }
              }
            }
          });
          document.dispatchEvent(variantUpdateEvent);
        }

        document.dispatchEvent(new CustomEvent("overdose:product-stitching-loaded"));
      });
    }
  }
  
  runOnceAfterUpdate() {
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 0);
      });
    }).then(() => {
      this.attachColorSwatchEventListeners();
    });
  }
  
  attachColorSwatchEventListeners(products) {
    const self = this;
    const swatches = self.querySelectorAll('.product-stitching__color-list li');
    if (swatches.length > 0) {
      swatches.forEach(item => {
        item.addEventListener("click", function (event) {
          event.preventDefault();
          const productHandle = this.querySelector('[data-handle]').getAttribute('data-handle');
          const productUrl = this.querySelector('[data-href]').getAttribute('data-href');
          const productId = this.querySelector('[data-product-id]').getAttribute('data-product-id');
          
          if (self.template === 'product') {
            if (self.isBundleTemplate !== true) {
              const urlParts = productUrl.split('?');
              const productPath = urlParts[0];
              history.pushState({path: productPath}, '', productPath);
            }
            self.updateSection(productHandle);
          } else {
            products.forEach((product, index) => {
              if (product.id.toString() === productId) {
                self.replaceImages(product, event);
              }
            });
          }
        });
      });
    }
    
  }
  
  _createImageElement(media, classes, sizes) {
    const previewImage = media["preview_image"], image = new Image(previewImage["width"], previewImage["height"]);
    image.className = classes;
    image.alt = media["alt"];
    image.sizes = sizes;
    image.src = previewImage["src"];
    image.srcset = generateSrcset(previewImage, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800]);
    return image;
  }
  
  async replaceImages(product, event) {
    const productCard = event.currentTarget.closest('.product-grid-item');
    if (productCard) {
      const primaryMediaElement = productCard.querySelector(".image-primary"), secondaryMediaElement = productCard.querySelector(".image-secondary");
      const newPrimaryMediaElement = this._createImageElement(product["featured_media"], primaryMediaElement.className, primaryMediaElement.sizes);
      if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
        secondaryMediaElement ? secondaryMediaElement.replaceWith(this._createImageElement(product["media"][product["featured_media"]["position"]] || product["media"][1], secondaryMediaElement.className, secondaryMediaElement.sizes)) : null;
        await primaryMediaElement.animate({ opacity: [1, 0] }, { duration: 250, easing: "ease-in", fill: "forwards" }).finished;
        await new Promise((resolve) => newPrimaryMediaElement.complete ? resolve() : newPrimaryMediaElement.onload = () => resolve());
        primaryMediaElement.replaceWith(newPrimaryMediaElement);
        newPrimaryMediaElement.animate({ opacity: [0, 1] }, { duration: 250, easing: "ease-in" });
      }
    }
  }
  
  setCurrent() {
    const swatches = this.querySelectorAll('.product-stitching__color-list li');
    
    if (swatches.length > 0) {
      this.container.querySelector(`[data-handle="${this.dataset.productHandle}"]`)?.classList.add("selected");
    }
  }
};
if (!window.customElements.get("product-stitching")) {
  window.customElements.define("product-stitching", ProductStitching);
}